// Single source of truth for intent labels and colors in the UI.
//
// INTENT_OPTIONS is derived from the Zod Intent enum in @cxhub/shared so that
// adding a new intent value there propagates here automatically. The label
// map itself (INTENT_LABELS) lives in @cxhub/shared too — the server needs
// the same wording for feedback messages/logs, so it's a single source of
// truth rather than two copies drifting apart.
import { Intent, INTENT_LABELS, intentLabel } from '@cxhub/shared'

export { intentLabel }

// MUI Chip color for each intent value. teal/purple are custom palette
// colors added in theme.js — primary (blue) and info (also blue, unstyled
// in theme.js) are deliberately never used here: the app already leans on
// blue everywhere else, so blue intent chips get lost against it.
export const INTENT_COLOR = {
  order_status:         'default',    // gray
  cancellation:         'error',      // red
  return_refund:        'purple',
  return_status:        'success',    // green
  replacement:          'teal',
  resend_return_label:  'warning',    // orange
  fitment_confirmation: 'purple',
  fitment_preorder:     'purple',
  fitment_postorder:    'teal',       // post-sale, same family as replacement/product_defect
  product_question:     'teal',
  product_availability: 'success',    // green
  pricing:              'default',    // gray
  shipping_issue:       'error',      // red
  product_defect:       'teal',
  billing:              'warning',    // orange
  invoice_request:      'teal',       // low-urgency, same family as product_question/product_defect
  sales_hold:           'warning',    // orange
  sales_hold_phone_call: 'warning',   // orange
  negative_feedback:    'error',      // red -- a complaint that needs a human, same urgency color as cancellation/shipping_issue
  data_deletion_request: 'purple',    // distinct from the urgency reds/oranges -- a compliance matter, not a service issue
  confirm_refund_value: 'success',    // green, a refund check (task contacts only)
  update_replacement_tracking: 'teal', // replacement family (task contacts only)
  no_action_required:   'default',    // gray
  other:                'default',    // gray
  spam:                 'warning',    // orange
}

// [value, label] pairs for all intents, enum order + spam appended.
// spam is not in the Zod enum (it's a rule-engine outcome, not a classifier
// intent) so its VALUE is added manually here -- but its wording still comes
// from INTENT_LABELS, so it can't drift from the label the server uses.
// Use this for Select/MenuItem filter dropdowns.
export const INTENT_OPTIONS = [...Intent.options.map((v) => [v, INTENT_LABELS[v] ?? v]), ['spam', INTENT_LABELS.spam]]

// Retired intents (2026-10-01): still in the enum so existing contacts
// display, but never offered as a fresh pick. fitment_confirmation was split
// into fitment_preorder / fitment_postorder.
export const RETIRED_INTENTS = new Set(['fitment_confirmation'])

// INTENT_OPTIONS for a picker: every live intent, plus a retired one only when
// it is already in use — the current selection, or an intent some contact
// already has (inUse: any iterable of intent values). Sorted by label,
// ignoring case (2026-10-20), so every picker shows the same alphabetical
// order; INTENT_OPTIONS itself stays in enum order.
export function intentOptionsFor(inUse = []) {
  const used = new Set(inUse)
  return INTENT_OPTIONS
    .filter(([k]) => !RETIRED_INTENTS.has(k) || used.has(k))
    .sort(([, a], [, b]) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }))
}
